import { captureReactException } from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Error Boundary global da aplicação.
 *
 * Captura qualquer erro de renderização não tratado (incluindo os causados
 * por extensões de tradução do navegador mexendo no DOM fora do controle do
 * React) e mostra uma tela de fallback amigável em vez de deixar a tela em
 * branco.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Envia para o Sentry junto com o componentStack, que e o que diz QUAL
        // componente quebrou. Sem DSN configurado esta chamada e no-op.
        captureReactException(error, errorInfo);

        // eslint-disable-next-line no-console
        console.error('Erro não tratado capturado pelo ErrorBoundary:', error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className={styles.overlay}>
                    <div className={styles.card}>
                        <h1 className={styles.title}>Ops, algo deu errado</h1>
                        <p className={styles.text}>
                            Ocorreu um erro inesperado ao carregar esta página. Recarregue para continuar.
                        </p>
                        <Button onClick={this.handleReload}>Recarregar página</Button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
